import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActions, 
  Chip, 
  IconButton 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

interface Recipe {
  id: string;
  title: string;
  instructions?: string;
  ingredients: string[];
}

interface RecipeCardProps {
  recipe: Recipe;
  onEdit: (recipe: Recipe) => void;
  onDelete: (recipe: Recipe) => void;
}

const RecipeCard: React.FC<RecipeCardProps> = ({ recipe, onEdit, onDelete }) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
          {recipe.title}
        </Typography>
        
        {recipe.ingredients && recipe.ingredients.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Ingredients:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {recipe.ingredients.map((ing) => (
                <Chip key={ing} label={ing} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {recipe.instructions && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Instructions:
            </Typography>
            <Typography 
              variant="body2" 
              color="text.primary" 
              sx={{ 
                whiteSpace: 'pre-line',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {recipe.instructions}
            </Typography>
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider', px: 2, py: 1 }}>
        <IconButton onClick={() => onEdit(recipe)} color="primary" size="small" aria-label="edit recipe">
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => onDelete(recipe)} color="error" size="small" aria-label="delete recipe">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
};

export default RecipeCard;
